import {
  Box,
  Button,
  Fab,
  Grid,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  LinearProgress,
  Divider,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  customerSelector,
  getCustomerThunk,
} from "store/reducers/user_profiles/customerSlice";
import CustomerProfileItem from "./CustomerProfileItem";
import CustomerProfileUpdateDialog from "./CustomerProfileUpdateDialog";
import { Refresh } from "@mui/icons-material";
import CustomDialog from "components/CustomDialog";
import BottomDialog from "components/BottomDialog";
import useDownloader from "react-use-downloader";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import { baseUrl } from "data/server_apis/server_api";
import moment from "moment";

const CustomerProfileList = () => {
  const dispatch = useDispatch();
  const customers = useSelector(customerSelector.selectAll);
  const { status, totalPages } = useSelector((state) => state.customers);
  const [page, setPage] = React.useState(1);
  const [customer, setCustomer] = useState(null);
  const [filters, setFilters] = useState({
    secondary_phone: "",
    first_name: "",
    last_name: "",
    employee_id: "",
  });
  const [searchFilters, setSearchFilters] = useState(filters);

  useEffect(() => {
    dispatch(getCustomerThunk({ page, filters: searchFilters }));
  }, [page, searchFilters, dispatch]);

  const handleChange = (event, value) => {
    setPage(value);
  };
  const [openUpdate, setOpenUpdate] = useState(false);

  const updateItem = (customer) => {
    setCustomer(customer);
    setOpenUpdate(true);
  };

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSearch = () => {
    setSearchFilters(filters);
    setPage(1);
  };
  // Set default fromDate to start of current month, toDate to today
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      999
    );
  });
  const token = useSelector((state) => state.auth?.user?.access);
  const { elapsed, download, cancel, error, isInProgress } = useDownloader({
    mode: "cors",
    credentials: "include",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const handleExport = () => {
    setOpenExportDialog(true);
  };

  const handleExportDownload = () => {
    setOpenExportDialog(false);
    const from = moment(fromDate).toISOString();
    const to = moment(toDate).toISOString();
    const filename = `${fromDate.toISOString().replace(/[-:.]/g, "")}_${toDate
      .toISOString()
      .replace(/[-:.]/g, "")}-balance-export.csv`;
    download(
      `${baseUrl}/payments/customer-balance/get-balance-sheet-csv?from_date_time=${from}&to_date_time=${to}`,
      filename
    );
  };

  return (
    <Box padding={2}>
      <Grid container spacing={2} mb={2} alignItems="center">
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            name="secondary_phone"
            label="Phone"
            variant="outlined"
            size="small"
            value={filters.secondary_phone}
            onChange={handleFilterChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            name="first_name"
            label="First Name"
            variant="outlined"
            size="small"
            value={filters.first_name}
            onChange={handleFilterChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            name="last_name"
            label="Last Name"
            variant="outlined"
            size="small"
            value={filters.last_name}
            onChange={handleFilterChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            name="employee_id"
            label="Employee ID"
            variant="outlined"
            size="small"
            value={filters.employee_id}
            onChange={handleFilterChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            fullWidth
            sx={{ height: 40 }}
          >
            Search
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleExport}
            fullWidth
            sx={{ height: 40 }}
            startIcon={<FileDownloadIcon />}
          >
            Balance sheet
          </Button>
        </Grid>
      </Grid>
      {/* Export Balance Dialog */}
      <CustomDialog
        open={openExportDialog}
        handleClose={() => setOpenExportDialog(false)}
        heading="Export Customer Balance"
      >
        <Box p={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={2}>
              <DateTimePicker
                label="From Date & Time"
                value={fromDate}
                onChange={setFromDate}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
              <DateTimePicker
                label="To Date & Time"
                value={toDate}
                onChange={setToDate}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleExportDownload}
                fullWidth
              >
                Download CSV
              </Button>
            </Stack>
          </LocalizationProvider>
        </Box>
      </CustomDialog>
      <Box
        sx={{
          backgroundColor: "#e7ebf0",
          borderRadius: 2,
          flex: 1,
        }}
        padding={1}
      >
        <TableContainer component={Paper}>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>ID </TableCell>
                <TableCell align="left">First name </TableCell>
                <TableCell align="left">Last name </TableCell>
                <TableCell align="center" sx={{ minWidth: 80 }}>
                  Employee id
                </TableCell>
                <TableCell align="center">Phone</TableCell>
                <TableCell align="center">Balance</TableCell>
                <TableCell align="center">Location</TableCell>
                <TableCell align="center">Biometric</TableCell>
                <TableCell align="center">RFID</TableCell>
                <TableCell align="center">Aadhaar</TableCell>
                <TableCell align="center">&nbsp;</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {status?.loading === "get" ? (
                <>
                  {Array(30)
                    .fill("")
                    .map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        {Array(10)
                          .fill("")
                          .map((i, j) => (
                            <TableCell
                              align="center"
                              component="th"
                              scope="row"
                              key={j}
                            >
                              <Skeleton animation="wave" />
                            </TableCell>
                          ))}
                      </TableRow>
                    ))}
                </>
              ) : (
                <>
                  {customers.map((item) => (
                    <CustomerProfileItem
                      customer={item}
                      key={item.id}
                      update={updateItem}
                    />
                  ))}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
        <Pagination
          count={totalPages}
          variant="outlined"
          shape="rounded"
          onChange={handleChange}
        />
      </Stack>
      {openUpdate && (
        <CustomerProfileUpdateDialog
          open={openUpdate}
          handleClose={() => {
            setOpenUpdate(false);
          }}
          customer={customer}
        />
      )}
      {/* Download Progress Dialog */}
      <BottomDialog
        open={isInProgress}
        onClose={() => setOpenExportDialog(false)}
      >
        <Stack direction="column">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h5" sx={{ ml: 2 }}>
              Downloading balance sheet
            </Typography>
            <IconButton onClick={() => setOpenExportDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />
          <Box sx={{ m: 2 }}>
            <LinearProgress />
          </Box>
          <Typography variant="caption" sx={{ mx: 2 }}>
            Time elapsed: {elapsed} Seconds
          </Typography>
          <Stack direction="row">
            <Box sx={{ ml: 2, mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={cancel}
              >
                Cancel download
              </Button>
            </Box>
            {error && (
              <Typography variant="subtitle1" color="red">
                Error occurred
              </Typography>
            )}
          </Stack>
        </Stack>
      </BottomDialog>
      <Fab
        sx={{
          margin: 0,
          top: "auto",
          right: { xs: 40, sm: 30, md: 50, lg: 70, xl: 70 },
          bottom: 30,
          left: "auto",
          position: "fixed",
        }}
        size="medium"
        color="primary"
        onClick={() => {
          dispatch(getCustomerThunk({ page, filters: searchFilters }));
        }}
      >
        <Refresh />
      </Fab>
    </Box>
  );
};

export default CustomerProfileList;
